import React, { useMemo } from 'react';
import { Box, Inscription, LinkButton, List, PaginationBar, SelectDropdown } from '@common-fe/common-fe';

import { useBankingBalancesQuery } from '@/modules/employer/components/BankingBalance/queries/useBankingBalances.query';
import { useOrganizationBalanceQuery } from '@/modules/employer/components/BankingBalance/queries/useOrganizationBalance.query';

import SingleOrganizationSelector from '../SingleOrganizationSelector/SingleOrganizationSelector';

import BankingBalanceListItem from './BankingBalanceListItem';
import BankingBalanceStatsHeader from './BankingBalanceStatsHeader';
import useBankingBalanceList, { STATUS_OPTIONS } from './useBankingBalanceList';

const BankingBalanceList: React.FC = () => {
  const {
    displayOrg,
    isChildrenLeafLevel,
    page,
    setPage,
    perPage,
    setPerPage,
    statuses,
    queryStatuses,
    setStatuses,
    selectedOrg,
    handleOrgChange,
    handleShowChildren,
    handleViewOrgDetails,
    handleViewSelectedOrgDetails,
  } = useBankingBalanceList();

  const { data: statsData, logoUrl: statsLogoUrl } = useOrganizationBalanceQuery(displayOrg?.id);

  const { data, total, isLoading } = useBankingBalancesQuery({
    parentOrgId: displayOrg?.id,
    statuses: queryStatuses,
    page: page - 1,
    size: perPage,
  });

  const isEmpty = useMemo(() => !isLoading && data.length === 0, [isLoading, data.length]);

  return (
    <Box data-testid="banking-balance-list" direction="column">
      <BankingBalanceStatsHeader
        orgId={displayOrg.id}
        orgName={displayOrg?.name}
        orgType={displayOrg?.type}
        logoUrl={statsLogoUrl}
        hsaBalance={statsData?.hsa_balance}
        notionalBalance={statsData?.notional_balance}
      />

      <Box
        data-testid="banking-balance-list_wrapper"
        pad="spacing24"
        background="module"
        round="container1Round"
      >
        <Box direction="row" align="center" justify="between" margin={{ bottom: 'spacing24' }}>
          <Inscription weight="bold" color="textBody">
            Organizations: <Inscription data-testid="banking-balance-list_total">{total}</Inscription>
          </Inscription>
          <Box direction="row" gap="spacing12" align="center">
            <Box width="180px">
              <SelectDropdown
                id="status_filter"
                name="Status"
                prefix="Status"
                testId="banking_balance_status_filter"
                values={statuses}
                onChangeValues={(vals) => setStatuses(vals as string[])}
                options={STATUS_OPTIONS}
                activeTitle
                ellipsisMode
                allMode
                condensedTitle
              />
            </Box>
            <SingleOrganizationSelector
              testId="banking_balance"
              value={selectedOrg}
              onChange={handleOrgChange}
            />
          </Box>
        </Box>

        <List>
          {!isEmpty && (
            <List.Header>
              <Box style={{ flex: 4 }}>
                <Inscription color="textBody" size="small" weight="bold">Name</Inscription>
              </Box>
              <List.Header.Item flex={1.5}>HSA balance</List.Header.Item>
              <List.Header.Item flex={2}>Notional balance</List.Header.Item>
              <List.Header.Item flex={1} textAlign="end">Status</List.Header.Item>
              <Box width="24px" margin={{ left: 'spacing24' }}/>
            </List.Header>
          )}

          {isLoading ? (
            <List.Preloader testId="banking-balance-list_preloader" count={3} />
          ) : isEmpty ? (
            <Box pad={{ vertical: 'spacing24' }} gap="spacing8" align="center">
              <Inscription weight="bold" data-testid="banking-balance-list_empty-text">
                No sub-organizations for {displayOrg?.name}
              </Inscription>
              {selectedOrg && (
                <LinkButton
                  testId="banking-balance-list_view-details-link"
                  onClick={handleViewSelectedOrgDetails}
                  linkInlineMode
                >
                  View {selectedOrg.name} Details
                </LinkButton>
              )}
            </Box>
          ) : (
            data.map((item) => (
              <BankingBalanceListItem
                key={item.orgId}
                {...item}
                onShowChildren={handleShowChildren}
                onViewDetails={handleViewOrgDetails}
                hideShowChildren={isChildrenLeafLevel}
              />
            ))
          )}
        </List>

        {!isEmpty ? (
          <Box margin={{ top: 'spacing24' }}>
            <PaginationBar
              page={page}
              total={total}
              pageSize={perPage}
              onChangePage={setPage}
              onChangePageSize={setPerPage}
            />
          </Box>) : null}
      </Box>
    </Box>
  );
};

export default BankingBalanceList;
